import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore.jsx';
import { updateMe, uploadAvatar, deleteAvatar, deleteAccount,
         getUploadToken, generateUploadToken, revokeUploadToken } from '../api/users';
import { getDriveStatus, connectDrive, disconnectDrive, getStorageStats, migrateToDrive } from '../api/drive';
import FolderImport from '../components/images/FolderImport';

export default function SettingsPage() {
  return (
    <div className="max-w-xl space-y-8">
      <h1 className="text-2xl font-bold text-white">Settings</h1>
      <ProfileSection />
      <DriveSection />
      <FolderImportSection />
      <MobileUploadSection />
      <PasswordSection />
      <DangerSection />
    </div>
  );
}

// ── Auto-upload token (phone / Tasker ingest) ─────────────────────────────────
function MobileUploadSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['upload-token'], queryFn: () => getUploadToken().then((r) => r.data) });
  const token = data?.token || null;
  const [copied, setCopied] = useState('');

  const gen = useMutation({
    mutationFn: () => generateUploadToken(),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['upload-token'] }),
  });
  const revoke = useMutation({
    mutationFn: () => revokeUploadToken(),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['upload-token'] }),
  });

  const ingestUrl = `${window.location.origin}/api/images/ingest`;
  const copy = (text, what) => navigator.clipboard.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(''), 1500); });

  return (
    <section className="card p-6 space-y-4">
      <h2 className="font-semibold text-white">Auto-upload (phone / automation)</h2>
      <p className="text-xs text-gray-500">
        Use an upload key with Tasker (Android) or Shortcuts (iOS) to send screenshots to SnapVault automatically.
        The key can only upload — it can’t read or delete — and you can revoke it anytime.
      </p>

      <div>
        <label className="label">Upload URL</label>
        <div className="flex gap-1.5">
          <input readOnly className="input text-xs font-mono flex-1" value={ingestUrl} />
          <button className="btn-secondary text-xs px-2 shrink-0" onClick={() => copy(ingestUrl, 'url')}>{copied === 'url' ? '✓' : 'Copy'}</button>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">POST multipart · field name <span className="font-mono">image</span> · header <span className="font-mono">X-Upload-Token</span> · optional field <span className="font-mono">client_key</span> (dedup).</p>
      </div>

      <div>
        <label className="label">Upload key</label>
        {token ? (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              <input readOnly className="input text-xs font-mono flex-1" value={token} />
              <button className="btn-secondary text-xs px-2 shrink-0" onClick={() => copy(token, 'key')}>{copied === 'key' ? '✓' : 'Copy'}</button>
            </div>
            <div className="flex gap-3">
              <button className="text-xs text-gray-400 hover:text-white" onClick={() => gen.mutate()} disabled={gen.isPending}>Regenerate</button>
              <button className="text-xs text-red-400 hover:text-red-300" onClick={() => revoke.mutate()} disabled={revoke.isPending}>Revoke</button>
            </div>
          </div>
        ) : (
          <button className="btn-primary text-sm" onClick={() => gen.mutate()} disabled={gen.isPending}>
            {gen.isPending ? 'Generating…' : 'Generate upload key'}
          </button>
        )}
      </div>
    </section>
  );
}

// ── Import from a folder (only new files, keeps names) ────────────────────────
function FolderImportSection() {
  return (
    <section className="card p-6 space-y-4">
      <h2 className="font-semibold text-white">Import from a folder</h2>
      <FolderImport />
    </section>
  );
}

// ── Google Drive (per-user storage) ───────────────────────────────────────────
function DriveSection() {
  const qc = useQueryClient();
  const { data: drive } = useQuery({ queryKey: ['drive', 'status'],  queryFn: () => getDriveStatus().then((r) => r.data) });
  const { data: stats } = useQuery({ queryKey: ['drive', 'storage'], queryFn: () => getStorageStats().then((r) => r.data) });

  const [migrating, setMigrating]   = useState(false);
  const [progress, setProgress]     = useState(null);
  const [migrateErr, setMigrateErr] = useState('');
  const mb = (b) => (b / 1024 / 1024).toFixed(1);

  const disc = useMutation({
    mutationFn: () => disconnectDrive(),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['drive', 'status'] }),
  });
  async function connect() {
    const r = await connectDrive();
    window.location.href = r.data.url; // Google consent, returns to /settings
  }

  async function migrate() {
    setMigrating(true); setMigrateErr(''); setProgress({ migrated: 0, remaining: stats?.local?.files ?? 0 });
    let total = 0;
    try {
      for (;;) {
        const { data } = await migrateToDrive(10);
        total += data.migrated;
        setProgress({ migrated: total, remaining: data.remaining });
        if (data.migrated === 0) break;
      }
      qc.invalidateQueries({ queryKey: ['drive', 'storage'] });
      qc.invalidateQueries({ queryKey: ['images'] });
    } catch (e) {
      setMigrateErr(e.response?.data?.error || 'Migration stopped — you can run it again.');
    } finally {
      setMigrating(false);
    }
  }

  const driveResult = new URLSearchParams(window.location.search).get('drive');
  const resultMsg = {
    connected: ['Google Drive connected.', 'text-green-400'],
    denied:    ['You declined the Google permission.', 'text-yellow-400'],
    failed:    ['Drive connection failed — try again in a minute.', 'text-red-400'],
    badstate:  ['Connection expired — try again.', 'text-red-400'],
  }[driveResult];

  return (
    <section className="card p-6 space-y-4">
      <h2 className="font-semibold text-white">Google Drive storage</h2>
      {resultMsg && <p className={`text-sm ${resultMsg[1]}`}>{resultMsg[0]}</p>}
      <p className="text-xs text-gray-500">
        Connect your Google Drive to keep your uploaded originals in your own Drive (free 15 GB). Thumbnails stay on SnapVault.
        Applies to new uploads while the site storage is set to Google Drive.
      </p>

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-300">
          {drive?.connected
            ? `Connected as ${drive.email || 'your account'} · folder “${drive.folder || 'SnapVault'}”`
            : 'Not connected'}
        </p>
        {drive?.connected ? (
          <button className="btn-secondary text-sm" onClick={() => disc.mutate()} disabled={disc.isPending}>
            {disc.isPending ? 'Disconnecting…' : 'Disconnect'}
          </button>
        ) : (
          <button className="btn-primary text-sm" onClick={connect}>Connect Google Drive</button>
        )}
      </div>

      {stats && (
        <div className="border-t border-gray-800 pt-4 space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
            <span>Local: <span className="text-gray-200">{stats.local.files} files · {mb(stats.local.bytes)} MB</span></span>
            <span>Drive: <span className="text-gray-200">{stats.drive.files} files · {mb(stats.drive.bytes)} MB</span></span>
          </div>
          {drive?.connected && stats.local.files > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <button className="btn-secondary text-sm" onClick={migrate} disabled={migrating}>
                {migrating ? 'Migrating…' : `↑ Move my ${stats.local.files} local file${stats.local.files !== 1 ? 's' : ''} to Drive`}
              </button>
              {migrating && progress && (
                <span className="text-xs text-gray-400">{progress.migrated} moved · {progress.remaining} left</span>
              )}
              {!migrating && progress && !migrateErr && (
                <span className="text-xs text-green-400">Done · {progress.migrated} moved{progress.remaining ? ` · ${progress.remaining} couldn’t be moved (no local file)` : ''}</span>
              )}
              {migrateErr && <span className="text-xs text-red-400">{migrateErr}</span>}
            </div>
          )}
          <p className="text-[11px] text-gray-500">
            One-way: each local original is uploaded to your Drive and the local copy is freed. Thumbnails stay. Safe to run repeatedly.
          </p>
        </div>
      )}
    </section>
  );
}

// ── Profile ───────────────────────────────────────────────────────────────────
function ProfileSection() {
  const { user, reload }            = useAuth();
  const qc                          = useQueryClient();
  const [name,    setName]          = useState(user?.display_name || '');
  const [saved,   setSaved]         = useState(false);
  const [err,     setErr]           = useState('');
  const fileRef                     = useRef(null);

  const nameMut = useMutation({
    mutationFn: (display_name) => updateMe({ display_name }),
    onSuccess: async () => {
      await reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setErr(e.response?.data?.error || 'Update failed.'),
  });

  const avatarMut = useMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('avatar', file);
      return uploadAvatar(form);
    },
    onSuccess: () => reload(),
    onError:   (e) => setErr(e.response?.data?.error || 'Avatar upload failed.'),
  });

  const rmAvatarMut = useMutation({
    mutationFn: deleteAvatar,
    onSuccess:  () => reload(),
  });

  const avatarSrc = user?.avatar_url || null;

  return (
    <section className="card p-6 space-y-5">
      <h2 className="font-semibold text-white">Profile</h2>

      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={user?.display_name}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-700"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-brand-700 flex items-center justify-center text-white text-2xl font-bold border-2 border-gray-700">
              {(user?.display_name?.[0] || '?').toUpperCase()}
            </div>
          )}
          {avatarMut.isPending && (
            <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <div className="flex gap-2">
            <button className="btn-secondary text-xs px-3 py-1.5" onClick={() => fileRef.current?.click()}>
              Change photo
            </button>
            {avatarSrc && (
              <button
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
                onClick={() => rmAvatarMut.mutate()}
              >
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">JPG, PNG, WebP · max 5 MB · cropped to 200×200</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files[0] && avatarMut.mutate(e.target.files[0])}
          />
        </div>
      </div>

      {/* Display name */}
      <form
        onSubmit={(e) => { e.preventDefault(); setErr(''); nameMut.mutate(name); }}
        className="space-y-3"
      >
        <div>
          <label className="label" htmlFor="display_name">Display name</label>
          <input
            id="display_name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input opacity-60" value={user?.email || ''} readOnly />
          <p className="text-xs text-gray-600 mt-1">Email cannot be changed.</p>
        </div>
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={nameMut.isPending}>
            {nameMut.isPending ? 'Saving…' : 'Save profile'}
          </button>
          {saved && <span className="text-green-400 text-sm">Saved!</span>}
        </div>
      </form>
    </section>
  );
}

// ── Password ──────────────────────────────────────────────────────────────────
function PasswordSection() {
  const { user }  = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [err,  setErr]  = useState('');
  const [ok,   setOk]   = useState(false);

  const mutation = useMutation({
    mutationFn: (data) => updateMe(data),
    onSuccess:  () => {
      setForm({ current_password: '', new_password: '', confirm: '' });
      setOk(true);
      setTimeout(() => setOk(false), 3000);
    },
    onError: (e) => setErr(e.response?.data?.error || 'Password change failed.'),
  });

  // Google-only accounts have no password
  if (user?.google_id && !user?.password_hash) {
    return (
      <section className="card p-6 space-y-2">
        <h2 className="font-semibold text-white">Password</h2>
        <p className="text-sm text-gray-400">Your account uses Google sign-in — no password is set.</p>
      </section>
    );
  }

  function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (form.new_password !== form.confirm) return setErr('New passwords do not match.');
    if (form.new_password.length < 8) return setErr('New password must be at least 8 characters.');
    mutation.mutate({ current_password: form.current_password, new_password: form.new_password });
  }

  return (
    <section className="card p-6 space-y-4">
      <h2 className="font-semibold text-white">Change password</h2>
      <form onSubmit={handleSubmit} className="space-y-3">
        {[
          { id: 'current_password', label: 'Current password',  key: 'current_password', ac: 'current-password' },
          { id: 'new_password',     label: 'New password',      key: 'new_password',     ac: 'new-password' },
          { id: 'confirm',          label: 'Confirm new password', key: 'confirm',        ac: 'new-password' },
        ].map(({ id, label, key, ac }) => (
          <div key={id}>
            <label className="label" htmlFor={id}>{label}</label>
            <input
              id={id}
              type="password"
              autoComplete={ac}
              className="input"
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              required
              minLength={key === 'current_password' ? undefined : 8}
            />
          </div>
        ))}
        {err && <p className="text-red-400 text-sm">{err}</p>}
        {ok  && <p className="text-green-400 text-sm">Password updated!</p>}
        <button type="submit" className="btn-primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </section>
  );
}

// ── Danger zone ───────────────────────────────────────────────────────────────
function DangerSection() {
  const { logout }                  = useAuth();
  const navigate                    = useNavigate();
  const [confirm, setConfirm]       = useState('');
  const [password, setPassword]     = useState('');
  const [open,    setOpen]          = useState(false);
  const [err,     setErr]           = useState('');

  const mutation = useMutation({
    mutationFn: () => deleteAccount({ password: password || undefined }),
    onSuccess:  () => { logout(); navigate('/login'); },
    onError:    (e) => setErr(e.response?.data?.error || 'Deletion failed.'),
  });

  return (
    <section className="card p-6 space-y-4 border-red-900/50">
      <h2 className="font-semibold text-red-400">Danger zone</h2>
      {!open ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-300 font-medium">Delete account</p>
            <p className="text-xs text-gray-500">Permanently delete your account and all your images.</p>
          </div>
          <button className="btn-secondary border-red-900 text-red-400 text-sm" onClick={() => setOpen(true)}>
            Delete account
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            This will permanently delete your account, all images, folders, and categories.
            <strong className="text-red-400"> This cannot be undone.</strong>
          </p>
          <div>
            <label className="label">Type <span className="text-white font-mono">delete my account</span> to confirm</label>
            <input
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="delete my account"
            />
          </div>
          <div>
            <label className="label">Your password (if set)</label>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <div className="flex gap-3">
            <button
              className="bg-red-600 hover:bg-red-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              disabled={confirm !== 'delete my account' || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? 'Deleting…' : 'Permanently delete account'}
            </button>
            <button className="btn-secondary text-sm" onClick={() => { setOpen(false); setErr(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Spinner({ size = 'md' }) {
  const cls = size === 'sm' ? 'w-4 h-4' : 'w-6 h-6';
  return <div className={`${cls} border-2 border-brand-500 border-t-transparent rounded-full animate-spin`} />;
}
